import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, Search, RefreshCw, Building2, Clock, DollarSign, Bookmark, BookmarkCheck, GraduationCap, Package, Flag } from "lucide-react";

function timeAgo(unix) {
  if (!unix) return "";
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff/86400)}d ago`;
  return `${Math.floor(diff/604800)}w ago`;
}

const SOURCE_STYLES = {
  "RemoteOK":       { bg: "#0F3D2E", fg: "#FBF9F5" },
  "Arbeitnow":      { bg: "#D96B27", fg: "#fff" },
  "WeWorkRemotely": { bg: "#1A1D1E", fg: "#FBF9F5" },
};

function TrackDialog({ gig, open, onOpenChange, onDone }) {
  const [hustles, setHustles] = useState([]);
  const [saved, setSaved] = useState([]);
  const [hustleId, setHustleId] = useState("");
  const [status, setStatus] = useState("saved");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/hustles").then(r => setHustles(r.data));
    api.get("/saved").then(r => setSaved(r.data.hustles));
  }, [open]);

  const savedIdSet = useMemo(() => new Set(saved.map(s => s.id)), [saved]);
  const nonSavedHustles = useMemo(
    () => hustles.filter(h => !savedIdSet.has(h.id)),
    [hustles, savedIdSet]
  );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/applications", {
        gig_id: gig.id,
        gig: {
          title: gig.title, source: gig.source, category: gig.category,
          company: gig.company, url: gig.url, budget: gig.budget,
        },
        hustle_id: (hustleId && hustleId !== "none") ? hustleId : null,
        status,
      });
      toast.success("Tracked in your Applications tab");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="track-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Track this gig</DialogTitle></DialogHeader>
        <div className="text-sm text-[var(--stone)] mb-2 line-clamp-2">{gig?.title}</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm font-semibold">Link to a saved hustle (optional)</label>
            <Select value={hustleId} onValueChange={setHustleId}>
              <SelectTrigger data-testid="track-hustle-select"><SelectValue placeholder="Standalone"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Standalone (no hustle)</SelectItem>
                {saved.length > 0 && (
                  <>
                    <div className="px-2 py-1 caption">From your shortlist</div>
                    {saved.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                  </>
                )}
                {hustles.length > 0 && (
                  <>
                    <div className="px-2 py-1 caption border-t border-[var(--border-warm)] mt-1">All hustles</div>
                    {nonSavedHustles.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-semibold">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="track-status-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="saved">Saved for later</SelectItem>
                <SelectItem value="applied">Applied</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full justify-center" data-testid="track-submit-btn">
            {saving ? "Saving…" : "Track it"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function GigsTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [source, setSource] = useState("all");
  const [entryLevel, setEntryLevel] = useState(false);
  const [equipment, setEquipment] = useState(false);
  const [usOnly, setUsOnly] = useState(false);
  const [state, setState] = useState("ALL");
  const [trackGig, setTrackGig] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (q.trim()) params.set("q", q.trim());
      if (entryLevel) params.set("entry_level", "1");
      if (equipment) params.set("equipment_provided", "1");
      if (usOnly) params.set("country", "US");
      if (state !== "ALL") { params.set("country", "US"); params.set("state", state); }
      params.set("limit", "80");
      const { data } = await api.get(`/gigs?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, entryLevel, equipment, usOnly, state]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (source === "all") return data.gigs;
    return data.gigs.filter(g => g.source === source);
  }, [data, source]);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="caption flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--copper)] animate-pulse"></span>
            Live · updates every 15 min
          </div>
          <h2 className="font-display text-3xl font-black">Real paying gigs, right now</h2>
        </div>
        <button onClick={load} className="btn-outline !py-2 !px-3 text-sm" disabled={loading} data-testid="gigs-refresh-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--stone)]"/>
          <Input placeholder="Search title, tech, company…" value={q} onChange={(e)=>setQ(e.target.value)}
            className="pl-9" data-testid="gigs-search-input"/>
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[170px]" data-testid="gigs-category-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(data?.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[180px]" data-testid="gigs-source-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {(data?.sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={()=>setUsOnly(!usOnly)} data-testid="filter-us-btn"
          className={`tag-pill transition ${usOnly ? "!bg-[var(--forest)] !text-[var(--alabaster)] !border-[var(--forest)]" : ""}`}>
            <Flag size={12}/> US-based{data?.counts?.us_only ? ` (${data.counts.us_only})` : ""}
        </button>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className={`w-[190px] h-auto py-1.5 !text-xs !rounded-full ${state !== "ALL" ? "!bg-[var(--forest)] !text-[var(--alabaster)] !border-[var(--forest)]" : ""}`}
                         data-testid="filter-state-select">
            <SelectValue placeholder="Any US state"/>
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="ALL">Any US state</SelectItem>
            {(data?.us_states || []).map(([code, name]) => (
              <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button onClick={()=>setEntryLevel(!entryLevel)} data-testid="filter-entry-btn"
          className={`tag-pill transition ${entryLevel ? "!bg-[var(--forest)] !text-[var(--alabaster)] !border-[var(--forest)]" : ""}`}>
          <GraduationCap size={12}/> Entry level{data?.counts?.entry_level ? ` (${data.counts.entry_level})` : ""}
        </button>
        <button onClick={()=>setEquipment(!equipment)} data-testid="filter-equipment-btn"
          className={`tag-pill transition ${equipment ? "!bg-[var(--copper)] !text-white !border-[var(--copper)]" : ""}`}>
          <Package size={12}/> Equipment provided{data?.counts?.equipment_provided ? ` (${data.counts.equipment_provided})` : ""}
        </button>
        {(usOnly || entryLevel || equipment || state !== "ALL") && (
          <button onClick={()=>{setUsOnly(false); setEntryLevel(false); setEquipment(false); setState("ALL");}}
            className="tag-pill !border-dashed" data-testid="filter-clear-btn">
            Clear filters
          </button>
        )}
      </div>

      {loading && !data && <div className="py-12 text-center text-[var(--stone)]">Fetching live gigs…</div>}

      {data && (
        <>
          <div className="text-sm text-[var(--stone)] mb-4 font-mono">
            {filtered.length} gig{filtered.length===1?"":"s"} · from {(data.sources || []).length} source{(data.sources||[]).length===1?"":"s"}
          </div>
          <div className="grid gap-3">
            {filtered.map(g => {
              const style = SOURCE_STYLES[g.source] || { bg: "#6B7280", fg: "#fff" };
              return (
                <div key={g.id} className="card-warm p-5" data-testid={`gig-card-${g.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="tag-pill" style={{background: style.bg, color: style.fg, border: "none"}}>
                      {g.source}
                    </span>
                    <span className="tag-pill">{g.category}</span>
                    {g.country === "US" && <span className="tag-pill">🇺🇸 US</span>}
                    {g.is_entry_level && (
                      <span className="tag-pill" style={{background:"#0F3D2E", color:"#FBF9F5", border:"none"}}>
                        <GraduationCap size={10}/> Entry level
                      </span>
                    )}
                    {g.equipment_provided && (
                      <span className="tag-pill" style={{background:"#D96B27", color:"#fff", border:"none"}}>
                        <Package size={10}/> Equipment
                      </span>
                    )}
                    {g.remote && <span className="tag-pill">Remote</span>}
                    <span className="text-xs text-[var(--stone)] font-mono ml-auto"><Clock size={12} className="inline mr-1"/>{timeAgo(g.posted_at)}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold leading-tight">
                    {g.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-[var(--stone)]">
                    {g.company && <span className="flex items-center gap-1"><Building2 size={14}/> {g.company}</span>}
                    {g.budget && <span className="flex items-center gap-1 text-[var(--forest)] font-semibold font-mono"><DollarSign size={14}/>{g.budget}</span>}
                  </div>
                  {g.body && <p className="text-sm text-[var(--stone)] mt-2 line-clamp-2 break-words">{g.body}</p>}
                  <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                    <div className="flex flex-wrap gap-1">
                      {(g.tags || []).slice(0,5).map(t => <span key={t} className="tag-pill text-xs">{t}</span>)}
                    </div>
                    <div className="flex items-center gap-3">
                      {user && (
                        <button onClick={()=>setTrackGig(g)} className="text-xs font-semibold text-[var(--copper)] flex items-center gap-1"
                                data-testid={`gig-track-${g.id}`}>
                          <Bookmark size={14}/> Track
                        </button>
                      )}
                      <a href={g.url} target="_blank" rel="noopener noreferrer"
                         className="text-xs font-semibold text-[var(--forest)] flex items-center gap-1"
                         data-testid={`gig-apply-${g.id}`}>
                        Apply <ExternalLink size={12}/>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && !loading && (
            <div className="card-warm p-10 text-center">
              <div className="font-display text-xl font-bold">No matches.</div>
              <p className="text-[var(--stone)] mt-2">Try a different search or clear filters.</p>
            </div>
          )}
        </>
      )}
      {trackGig && (
        <TrackDialog gig={trackGig} open={!!trackGig} onOpenChange={(v)=>!v && setTrackGig(null)}/>
      )}
    </div>
  );
}
